import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";

// Import pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import Layout from "./components/layout/Layout";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/profile/ProfilePage";

// Import Client pages
import ClientIndexPage from "./pages/clients/IndexPage";
import CreateClientPage from "./pages/clients/CreatePage.jsx";
import EditClientPage from "./pages/clients/EditPage.jsx";

// Import user pages
import UsersIndexPage from "./pages/users/IndexPage";
import CreateUserPage from "./pages/users/CreatePage";
import EditUserPage from "./pages/users/EditPage";

// import site pages
import SiteIndexPage from "./pages/site/IndexPage";
import CreateSitePage from "./pages/site/CreatePage";
import EditSitePage from "./pages/site/EditPage";

// Import report pages
import ReportsIndexPage from "./pages/reports/IndexPage";
import CreateReportPage from "./pages/reports/CreatePage";
import EditReportPage from "./pages/reports/EditPage";
import EditValuesPage from "./pages/reports/EditValuesPage";
import ViewReportPage from "./pages/reports/ViewPage";
import StackPreportPage from "./pages/reports/StackPage";

// import mine type pages
import MineTypeIndexPage from "./pages/mineType/IndexPage";
import CategoryViewPage from "./pages/mineType/CategoryViewPage";
import CategoryEditPage from "./pages/mineType/CategoryEditPage";

// Import reviewer pages
import FieldEngineerReports from "./pages/FieldEngineerReports";
import FieldEngineerEditValuesPage from "./pages/FieldEngineerEditValuesPage";
import PeerReviewerReports from "./pages/PeerReviewerReports";
import SiteReviewerReports from "./pages/SiteReviewerReports";
import DirectorReviewerReports from "./pages/DirectorReviewerReports";
import NotFoundPage from "./pages/NotFoundPage";

// Route protection components
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return user ? <Navigate to="/dashboard" /> : children;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    <div className="">
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              {" "}
              <LoginPage />{" "}
            </PublicRoute>
          }
        />
        {/* <Route path="/register" element={<PublicRoute> <RegisterPage /> </PublicRoute>} /> */}
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              {" "}
              <ForgotPasswordPage />{" "}
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              {" "}
              <ResetPasswordPage />{" "}
            </PublicRoute>
          }
        />

        {/* Protected routes with layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              {" "}
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="profile" element={<ProfilePage />} />

          <Route path="dashboard" element={<DashboardPage />} />
          <Route
            path="settings"
            element={
              <AdminRoute>
                {" "}
                <SettingsPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="mine-types"
            element={
              <AdminRoute>
                {" "}
                <MineTypeIndexPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="mine-types/:id/category/view"
            element={
              <AdminRoute>
                {" "}
                <CategoryViewPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="mine-types/:id/category/edit"
            element={
              <AdminRoute>
                {" "}
                <CategoryEditPage />{" "}
              </AdminRoute>
            }
          />

          {/* Report management routes */}
          <Route
            path="reports"
            element={
              <AdminRoute>
                {" "}
                <ReportsIndexPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="reports/create"
            element={
              <AdminRoute>
                {" "}
                <CreateReportPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="reports/edit/:id"
            element={
              <AdminRoute>
                {" "}
                <EditReportPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="reports/edit-values/:id"
            element={
              <AdminRoute>
                {" "}
                <EditValuesPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="reports/view/:id"
            element={
              <AdminRoute>
                <ViewReportPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="reports/stack/:id"
            element={
              <AdminRoute>
                {" "}
                <StackPreportPage />{" "}
              </AdminRoute>
            }
          />

          {/* Client management routes */}
          <Route
            path="clients"
            element={
              <AdminRoute>
                {" "}
                <ClientIndexPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="clients/create"
            element={
              <AdminRoute>
                {" "}
                <CreateClientPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="clients/:id/edit"
            element={
              <AdminRoute>
                {" "}
                <EditClientPage />{" "}
              </AdminRoute>
            }
          />

          {/* User management routes */}
          <Route
            path="users"
            element={
              <AdminRoute>
                {" "}
                <UsersIndexPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="users/create"
            element={
              <AdminRoute>
                {" "}
                <CreateUserPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="users/:id/edit"
            element={
              <AdminRoute>
                {" "}
                <EditUserPage />{" "}
              </AdminRoute>
            }
          />

          {/* Sites management routes */}
          <Route
            path="site"
            element={
              <AdminRoute>
                {" "}
                <SiteIndexPage />{" "}
              </AdminRoute>
            }
          />
          <Route
            path="site/create"
            element={
              <AdminRoute>
                <CreateSitePage />
              </AdminRoute>
            }
          />
          <Route
            path="site/edit/:id"
            element={
              <AdminRoute>
                {" "}
                <EditSitePage />{" "}
              </AdminRoute>
            }
          />

          {/* Reviewer routes */}
          <Route
            path="field-engineer-reports"
            element={
              <ProtectedRoute>
                <FieldEngineerReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="field-engineer-reports/edit-values/:id"
            element={
              <ProtectedRoute>
                <FieldEngineerEditValuesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="peer-reviewer-reports"
            element={
              <ProtectedRoute>
                <PeerReviewerReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="site-reviewer-reports"
            element={
              <ProtectedRoute>
                <SiteReviewerReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="director-reviewer-reports"
            element={
              <ProtectedRoute>
                <DirectorReviewerReports />
              </ProtectedRoute>
            }
          />

          <Route path="not-found" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
          <Route index element={<Navigate to="/dashboard" />} />
        </Route>

        {/* Default 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

export default App;
